import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import PaymentPageForm from "./card"; // This is actually PaymentPage component from card.jsx
import PaymentServices from "../../services/PaymentServices";
import { logo } from "../imagepath";

/**
 * Test Payment Page - Bypasses authentication for testing
 * 
 * Usage:
 * /test-payment?amount=9000&totalAmount=9000&installmentAmount=750&planType=installment
 * 
 * Query Parameters:
 * - amount: First payment amount (default: 9000)
 * - totalAmount: Total amount (default: 9000)
 * - installmentAmount: Monthly installment amount (default: 750)
 * - planType: "monthly" or "annual" (default: "monthly")
 * - promoCode: Optional promo code
 */
const PaymentPageTest = () => {
  const [searchParams] = useSearchParams();
  
  const [showForm, setShowForm] = useState(true);
  const [purchaseType, setPurchaseType] = useState(() => {
    // Get from URL or default to "school"
    const urlType = searchParams.get("purchaseType");
    return urlType === "individual" ? "individual" : "school";
  });
  
  // Base amounts: Individual teachers: $900, Schools: $9000
  const baseAmount = purchaseType === "individual" ? 900 : 9000;
  
  // Get plan type and promo code from URL (or defaults)
  const planType = searchParams.get("planType") || "monthly";
  const promoCode = searchParams.get("promoCode") || null;
  
  // State for backend preview data
  const [backendPreview, setBackendPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState(null);
  
  // Set mock user role in localStorage based on purchase type
  useEffect(() => {
    if (purchaseType === "school") {
      localStorage.setItem("userrole", "1"); // School role
      localStorage.setItem("user", JSON.stringify({
        _id: "test-school-id",
        role: 1,
        name: "Test School",
        email: "test-school@example.com"
      }));
    } else {
      localStorage.setItem("userrole", "3"); // Individual teacher role
      localStorage.setItem("user", JSON.stringify({
        _id: "test-teacher-id",
        role: 3,
        name: "Test Teacher",
        email: "test-teacher@example.com"
      }));
    }
  }, [purchaseType]);
  
  // Fetch preview from backend when purchase type or plan type changes
  useEffect(() => {
    const fetchPreview = async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      
      try {
        // Ensure we have a token (use existing token if available)
        const existingToken = localStorage.getItem("token");
        if (!existingToken) {
          // For test page, we might not have a token - backend should handle this
          console.warn("No token found, preview may fail");
        }
        
        const previewResponse = await PaymentServices.previewPayment(
          baseAmount,
          promoCode,
          planType,
          false // isFromUrl
        );
        
        setBackendPreview(previewResponse);
        console.log("Backend preview response:", previewResponse);
      } catch (error) {
        console.error("Failed to fetch preview from backend:", error);
        setPreviewError(error.message || "Failed to fetch preview");
        setBackendPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    };
    
    // Small delay to ensure localStorage is updated
    const timer = setTimeout(() => {
      fetchPreview();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [purchaseType, planType, promoCode, baseAmount]);
  
  // Use backend preview data if available, otherwise fall back to URL params or defaults
  const getDefaultAmounts = (type) => {
    if (type === "individual") {
      const monthly = 900 / 12;
      const firstPayment = 900 - (Math.floor(monthly * 100) / 100 * 11);
      return {
        totalAmount: 900,
        firstPayment: Math.round(firstPayment * 100) / 100,
        installmentAmount: Math.floor(monthly * 100) / 100,
      };
    } else {
      const monthly = 9000 / 12;
      const firstPayment = 9000 - (Math.floor(monthly * 100) / 100 * 11);
      return {
        totalAmount: 9000,
        firstPayment: Math.round(firstPayment * 100) / 100,
        installmentAmount: Math.floor(monthly * 100) / 100,
      };
    }
  };
  
  // Determine amounts: Backend preview > URL params > defaults
  const defaultAmounts = getDefaultAmounts(purchaseType);
  const totalAmount = parseFloat(searchParams.get("totalAmount")) || 
                      (backendPreview?.totalAmount) || 
                      defaultAmounts.totalAmount;
  const firstPaymentAmount = parseFloat(searchParams.get("firstPayment")) || 
                             parseFloat(searchParams.get("amount")) || 
                             (backendPreview?.installmentBreakdown?.firstPayment) ||
                             defaultAmounts.firstPayment;
  const installmentAmount = parseFloat(searchParams.get("installmentAmount")) || 
                            (backendPreview?.installmentBreakdown?.monthlyPayment) ||
                            (backendPreview?.installmentAmount) ||
                            defaultAmounts.installmentAmount;
  
  const handleSuccess = (response, selectedPlanType) => {
    console.log("Payment Successful (Test)", response, selectedPlanType);
    console.log("Purchase Type:", purchaseType);
    setShowForm(false);
    alert(`Test Payment Successful!\n\nPurchase Type: ${purchaseType === "school" ? "School" : "Individual Teacher"}\nPlan: ${selectedPlanType}\nResponse: ${JSON.stringify(response, null, 2)}`);
  };
  
  return (
    <div className="main-wrapper">
      {/* Logo Header */}
      <div className="container mt-4 mb-3">
        <div className="row">
          <div className="col-12 text-center">
            <Link to="/">
              <img src={logo} alt="AngelWatch Logo" style={{ height: '50px', width: 'auto' }} />
            </Link>
          </div>
        </div>
      </div>
      <div className="page-content">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-10">
              <div className="card">
                <div className="card-header bg-info text-white">
                  <h4 className="mb-0">🧪 Test Payment Page</h4>
                  <small className="text-white-50">
                    This is a test page that bypasses authentication. Use query parameters to test different scenarios.
                  </small>
                </div>
                <div className="card-body">
                  {/* Purchase Type Toggle */}
                  <div className="mb-4">
                    <label className="form-label"><strong>Purchase Type:</strong></label>
                    <div className="btn-group w-100" role="group">
                      <input
                        type="radio"
                        className="btn-check"
                        name="purchaseType"
                        id="purchaseTypeSchool"
                        checked={purchaseType === "school"}
                        onChange={() => setPurchaseType("school")}
                      />
                      <label className="btn btn-outline-primary" htmlFor="purchaseTypeSchool">
                        🏫 School Purchase (Role 1)
                      </label>
                      
                      <input
                        type="radio"
                        className="btn-check"
                        name="purchaseType"
                        id="purchaseTypeIndividual"
                        checked={purchaseType === "individual"}
                        onChange={() => setPurchaseType("individual")}
                      />
                      <label className="btn btn-outline-primary" htmlFor="purchaseTypeIndividual">
                        👤 Individual Teacher Purchase (Role 3)
                      </label>
                    </div>
                    <small className="text-muted d-block mt-2">
                      This sets the user role in localStorage for testing. Currently set to: <strong>{purchaseType === "school" ? "School (Role 1)" : "Individual Teacher (Role 3)"}</strong>
                    </small>
                  </div>
                  
                  <div className="alert alert-info mb-4">
                    <h5>Test Parameters:</h5>
                    {previewLoading ? (
                      <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span>Fetching amounts from backend...</span>
                      </div>
                    ) : previewError ? (
                      <div className="alert alert-warning mb-0">
                        <strong>⚠️ Backend Preview Failed:</strong> {previewError}
                        <br />
                        <small>Using fallback values. Make sure you&apos;re logged in and the preview endpoint is available.</small>
                      </div>
                    ) : backendPreview ? (
                      <div className="alert alert-success mb-0">
                        <strong>✅ Using Backend Values</strong>
                      </div>
                    ) : null}
                    <ul className="mb-0 mt-3">
                      <li><strong>Purchase Type:</strong> {purchaseType === "school" ? "School (Role 1)" : "Individual Teacher (Role 3)"}</li>
                      <li><strong>Base Amount:</strong> ${baseAmount.toFixed(2)}</li>
                      <li><strong>Total Amount:</strong> ${totalAmount.toFixed(2)} {backendPreview && <span className="badge bg-success">from backend</span>}</li>
                      <li><strong>First Payment:</strong> ${firstPaymentAmount.toFixed(2)} {backendPreview?.installmentBreakdown && <span className="badge bg-success">from backend</span>}</li>
                      <li><strong>Monthly Payment:</strong> ${installmentAmount.toFixed(2)} {backendPreview?.installmentBreakdown && <span className="badge bg-success">from backend</span>}</li>
                      <li><strong>Plan Type:</strong> {planType}</li>
                      {promoCode && <li><strong>Promo Code:</strong> {promoCode}</li>}
                      {backendPreview?.discountPercent > 0 && (
                        <li><strong>Discount:</strong> {backendPreview.discountPercent}%</li>
                      )}
                    </ul>
                    <hr />
                    <div className="alert alert-info mb-0">
                      <strong>ℹ️ Note:</strong> Amounts are fetched from the backend preview endpoint based on your purchase type and role. 
                      URL parameters can override these values for manual testing.
                    </div>
                    <hr />
                    <p className="mb-0">
                      <strong>Example URLs:</strong><br />
                      <code>/test-payment?totalAmount=9000&firstPayment=750&installmentAmount=750&planType=installment&purchaseType=school</code><br />
                      <code>/test-payment?totalAmount=900&firstPayment=75&installmentAmount=75&planType=installment&purchaseType=individual</code><br />
                      <code>/test-payment?totalAmount=50&firstPayment=4.24&installmentAmount=4.16&planType=installment&promoCode=AW2025&purchaseType=school</code>
                    </p>
                  </div>
                  
                  {showForm ? (
                    <PaymentPageForm
                      amount={firstPaymentAmount}
                      totalAmount={totalAmount}
                      installmentAmount={installmentAmount}
                      reemcode={promoCode}
                      onSuccess={handleSuccess}
                      pageType="first_payment"
                    />
                  ) : (
                    <div className="text-center py-5">
                      <h3 className="text-success">✅ Test Payment Completed!</h3>
                      <p className="text-muted">Check the console for the full response.</p>
                      <button 
                        className="btn btn-primary"
                        onClick={() => {
                          setShowForm(true);
                          window.location.reload();
                        }}
                      >
                        Test Again
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPageTest;

